/* eslint-disable @khanacademy/ts-no-error-suppressions */
/**
 * Tabs on video and article pages beneath the content that allow users to submit
 * different discussion types.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";
import {connect} from "react-redux";

import Button from "@khanacademy/wonder-blocks-button";
import {Id, View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingSmall,
    LabelLarge,
} from "@khanacademy/wonder-blocks-typography";
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import Log, {Errors} from "@khan/logging";
import {mediaQueries} from "@khan/shared-styles";
import {getLoginUrl} from "@khan/urls";

import type {OfficialClarification} from "#clarifications-package/types.ts";
import TabbedPanel, {
    type Tab,
    type TabHeader,
} from "#components/tabbed-panel-package/tabbed-panel.tsx";
// TODO(diedra): Should we move the styles we're using from the content library
// tabs into a separate shared file? Maybe in the TabbedPanel package?
import * as sharedStyles from "#content-library-legacy/styles/shared.ts";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import type {ProfileModelType} from "#shared-package/profile-model.ts";
import ResendVerificationEmailButton from "#site-banners/banners/verify-email/resend-verification-email-button.tsx";

import * as sharedDiscussionStyles from "../shared-styles.ts";
import {getTabsStrings} from "../strings.ts";
import type {DiscussionContext, FeedbackType, ServerPost} from "../types.ts";
import {
    EnglishLink,
    getModifiedDiscussionContext,
    getUserUnverifiedEmail,
    loggedInQueryName,
    showMainDiscussionInput,
    userIsLoggedIn,
    userIsScratchpadAuthorViewingLoadingHelpRequests,
    userIsScratchpadAuthorViewingOpenHelpRequests,
    userIsScratchpadAuthorViewingQuestionsTab,
    userMeetsCommunityRequirements,
    userNotScratchpadAuthorViewingHelpRequests,
    userRestrictedPosting,
} from "../util.tsx";

import {changeTabAction, postSuccessAction} from "./discussion-actions.ts";
import DiscussionHeader from "./discussion-header.tsx";
import DiscussionInput from "./discussion-input.tsx";
import DiscussionListAcceptedClarifications from "./discussion-list-accepted-clarifications.tsx";
import DiscussionList from "./discussion-list.tsx";
import type {State} from "./discussion-reducer.ts";
import {DiscussionsNotAvailableMessage} from "./discussions-not-available-message.tsx";

type DefaultTabProps = {
    isTabSelected: boolean;
};

type TabProps = DefaultTabProps & {
    trigger: any;
    numTabs: number;
    tabNum: number;
};

class DiscussionTab extends React.Component<TabProps> {
    static defaultProps: DefaultTabProps = {
        isTabSelected: false,
    };

    render(): React.ReactNode {
        const {isTabSelected, trigger, numTabs, tabNum, ...rest} = this.props;

        return (
            <span
                className={css(
                    styles.notAButton,
                    styles.tabTrigger,
                    styles.tabSpan,
                    isTabSelected ? styles.activeTab : styles.inactiveTab,
                )}
                data-testid="discussion-tab"
                data-tab-num={tabNum}
                data-num-tabs={numTabs}
                {...rest}
            >
                <LabelLarge>{trigger}</LabelLarge>
            </span>
        );
    }
}

type TabsOwnProps = {
    acceptedClarificationPosts: Array<OfficialClarification>;
    contentKind: string;
    discussionContext: DiscussionContext;
    tab: FeedbackType;
    clarificationsEnabled: boolean;
    contentId: string;
    topicSlug: string;
};

type TabsReduxStateProps = {
    hasUnansweredHelpRequests?: boolean | null | undefined;
    currentTabKey: FeedbackType;
    focusOnTopLevelInput: boolean;
    shouldShowDiscussionsNotAvailableMessage: boolean;
};

type TabsReduxDispatchProps = {
    onPostSuccess: (post: ServerPost, tabType: FeedbackType) => void;
    onTabChanged: (newTabKey: FeedbackType, gqlClient: ApolloGqlClient) => void;
};

type TabsProps = TabsOwnProps &
    TabsReduxStateProps &
    TabsReduxDispatchProps &
    WithUserProfileProps &
    WithLocationProps &
    WithApolloProps;

type TabsState = {
    showMistakesTab: boolean;
};

const UpsellContainer = ({children}: {children: React.ReactNode}) => (
    <View style={styles.loginUpsell}>
        <View style={styles.upsellText}>
            <HeadingSmall tag="h2">
                {t`Want to join the conversation?`}
            </HeadingSmall>
        </View>
        <View style={styles.loginButtonContainer}>{children}</View>
    </View>
);

type TabElements = {
    key: FeedbackType;
    trigger: React.ReactNode;
    content: React.ReactNode;
};

class DiscussionTabs extends React.Component<TabsProps, TabsState> {
    // The tab nodes so we can focus on them on tab change.
    questionsTabNode: Element | null | undefined;
    commentsTabNode: Element | null | undefined;
    mistakesTabNode: Element | null | undefined;

    constructor(props: TabsProps) {
        super(props);

        const tabs = this.createTabs();

        if (tabs.length) {
            this.state = {
                showMistakesTab: false,
            };
        }
    }

    // TODO(kelsey): Investigate why state is set during component creation
    // and in the constructor
    state: TabsState = {
        showMistakesTab: false,
    };

    componentDidUpdate(prevProps: TabsProps) {
        const newTabKey: FeedbackType = this.props.currentTabKey;

        if (newTabKey === prevProps.currentTabKey) {
            return;
        }

        // If a user has followed a url with an qaExpandKey that belongs to a
        // clarification, we unfortunately don't know that the correct tab is
        // "Mistakes" until the first time the component updates.
        if (newTabKey === "clarifications" && !this.state.showMistakesTab) {
            this.setState({showMistakesTab: true});
            return;
        }
    }

    captureRef: (node: Element, tabKey: FeedbackType) => void = (
        node: Element,
        tabKey: FeedbackType,
    ) => {
        if (tabKey === "questions") {
            this.questionsTabNode = node;
        } else if (tabKey === "comments") {
            this.commentsTabNode = node;
        } else {
            this.mistakesTabNode = node;
        }
    };

    getTabNode: (tabKey: FeedbackType) => Element | null | undefined = (
        tabKey: FeedbackType,
    ) => {
        if (tabKey === "questions") {
            return this.questionsTabNode;
        }
        if (tabKey === "comments") {
            return this.commentsTabNode;
        }
        return this.mistakesTabNode;
    };

    handleReportMistake: () => void = () => {
        this.setState({
            showMistakesTab: true,
        });
        this.props.onTabChanged("clarifications", this.props.gqlClient);
    };

    onTabChangedwithGql: (key: FeedbackType) => void = (key: FeedbackType) => {
        this.props.onTabChanged(key, this.props.gqlClient);
    };

    // If a the user is able to add a top-level post on this page, we render the input field.
    // Otherwise, we render a message, if applicable, explaining why they're unable to post.
    renderInputFieldOrMessage: (
        profile: ProfileModelType | null | undefined,
        currentTab: FeedbackType,
    ) => React.ReactNode = (profile, currentTab) => {
        const {
            discussionContext,
            onPostSuccess,
            contentKind,
            hasUnansweredHelpRequests,
            focusOnTopLevelInput,
        } = this.props;
        const infoObject = getTabsStrings()[currentTab];

        if (!userIsLoggedIn()) {
            return this.renderLoginUpsell();
        }

        const meetsCommunityRequirements =
            profile && userMeetsCommunityRequirements(profile);

        const unverifiedEmail = getUserUnverifiedEmail(discussionContext);

        if (!meetsCommunityRequirements) {
            return this.renderPointsUnverifiedEmailAccountAgeUpsell(
                unverifiedEmail,
            );
        }

        // If the user is restricted from posting but doesn't match the above cases,
        // we don't want to explain why they can't post. Return nothing.
        if (userRestrictedPosting(discussionContext)) {
            return null;
        }

        if (
            userIsScratchpadAuthorViewingQuestionsTab(
                currentTab,
                contentKind,
                discussionContext,
                profile,
            )
        ) {
            // Users can't post questions on their own scratchpads.
            // TODO(diedra): Make helpRequestsLink string into a link
            // that takes the user to the Help Requests tab.

            const wantToAskQuestion = t`Want to ask a question to the community about improving your project?`;
            const helpRequestsLink = t`Head over to the help requests tab!`;
            const questionsTabForQuestions = t`We'll keep this area for questions that the community has for you about your project.`;

            return `${wantToAskQuestion} ${helpRequestsLink} ${questionsTabForQuestions}`;
        }

        if (
            userIsScratchpadAuthorViewingOpenHelpRequests(
                currentTab,
                contentKind,
                discussionContext,
                profile,
                hasUnansweredHelpRequests,
            )
        ) {
            // Users can only have one unanswered help request at a time.
            return t`To give everyone a fair chance at getting help, you can't request help again until your current request is answered.`;
        }

        if (
            userIsScratchpadAuthorViewingLoadingHelpRequests(
                currentTab,
                contentKind,
                discussionContext,
                profile,
                hasUnansweredHelpRequests,
            )
        ) {
            return <CircularSpinner size="medium" style={styles.spinner} />;
        }

        if (
            userNotScratchpadAuthorViewingHelpRequests(
                currentTab,
                contentKind,
                discussionContext,
                profile,
            )
        ) {
            // Only authors of a scratchpad can post help requests on
            // that scratchpad's page.
            return t`This area is reserved for help requests from this project's author.`;
        }

        if (
            !showMainDiscussionInput(
                currentTab,
                contentKind,
                discussionContext,
                profile,
                hasUnansweredHelpRequests,
            )
        ) {
            Log.error(
                "Not showing input field, message, or spinner in discussion tab area. There must be an edge case we're missing here.",
                Errors.Internal,
            );

            return null;
        }

        return (
            <DiscussionInput
                // @ts-expect-error - TS2783 - 'tab' is specified more than once, so this usage will be overwritten. | TS2532 - Object is possibly 'undefined'.
                tab={infoObject.key}
                // @ts-expect-error - TS2783 - 'onPostSuccess' is specified more than once, so this usage will be overwritten.
                onPostSuccess={onPostSuccess}
                onReportMistake={this.handleReportMistake}
                acceptingClarification={false}
                autoFocus={focusOnTopLevelInput}
                {...this.props}
            />
        );
    };

    getTabContent: (key: FeedbackType) => React.ReactNode = (
        key: FeedbackType,
    ) => {
        const {
            discussionContext,
            contentKind,
            tab,
            acceptedClarificationPosts,
            topicSlug,
            clarificationsEnabled,
            profile,
            hasUnansweredHelpRequests,
        } = this.props;

        // If the page has no tabs or input field, this top border is unnecessary as
        // it's only used to visually separate these elements.
        const showTopBorder = !discussionContext.restrictPosting;
        const showAcceptedClarifications =
            contentKind === "Video" &&
            tab === "clarifications" &&
            !!acceptedClarificationPosts.length;

        const modifiedDiscussionContext = getModifiedDiscussionContext(
            profile,
            discussionContext,
        );

        return (
            <View>
                {this.renderInputFieldOrMessage(profile, key)}

                {showTopBorder && (
                    <View style={[styles.separator, styles.topSeparator]} />
                )}

                {showAcceptedClarifications && (
                    <View>
                        <DiscussionListAcceptedClarifications />
                        <View style={styles.separator} />
                    </View>
                )}

                <DiscussionHeader />

                {/**
                    TODO(jeff, LP-6514): Cannot create
                    DiscussionList element because:
                    • Either property tab is missing in props [1] but
                        exists in DiscussionListProps [2].
                    • Or property tab is missing in props [1] but exists
                        in DiscussionListProps [3].
                  */}
                {/* @ts-expect-error - TS2741 - Property 'tab' is missing in type '{ topicSlug: string; discussionContext: DiscussionContext; clarificationsEnabled: boolean; onContentPage: true; hasUnansweredHelpRequests: boolean; }' but required in type 'ExportProps'. */}
                <DiscussionList
                    topicSlug={topicSlug}
                    discussionContext={modifiedDiscussionContext}
                    clarificationsEnabled={clarificationsEnabled}
                    onContentPage={true}
                    hasUnansweredHelpRequests={
                        hasUnansweredHelpRequests ?? false
                    }
                />

                <EnglishLink style={styles.englishLink} />
            </View>
        );
    };

    createTabs(): Array<TabElements> {
        const {discussionContext} = this.props;
        const tabsStrings = getTabsStrings();

        // @ts-expect-error - TS2322 - Type 'string[]' is not assignable to type 'FeedbackType[]'.
        const tabsCopyKeys: Array<FeedbackType> = Object.keys(tabsStrings);

        // Filter out clarifications tab if Mistakes tab is hidden and
        // filter out the projecthelp tab is this content doesn't allow project
        // feedback.
        const visibleTabsCopyKeys = tabsCopyKeys.filter((key: FeedbackType) => {
            return (
                (this.state.showMistakesTab || key !== "clarifications") &&
                (discussionContext.showProjectFeedback || key !== "projecthelp")
            );
        });

        return visibleTabsCopyKeys.map((key: FeedbackType) => {
            const infoObject = tabsStrings[key];
            const content = (
                <Body style={styles.tabContent}>{this.getTabContent(key)}</Body>
            );

            return {
                // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                key: infoObject.key,
                // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                trigger: infoObject.label,
                content: content,
            };
        });
    }

    createRenderers: (tabs: Array<TabElements>) => Array<Tab<FeedbackType>> = (
        tabs,
    ) => {
        const getTab = (
            {key, trigger, content}: TabElements,
            index: number,
        ): Tab<FeedbackType> => ({
            key,
            renderTab: (isTabSelected: boolean, props: any) => {
                const tabToRender: TabHeader = {
                    tabHeader: (
                        <DiscussionTab
                            numTabs={tabs.length}
                            // The tabNum is a human-readable number, not a computer-facing index,
                            // so we need to add 1 to the index to get it.
                            tabNum={index + 1}
                            // @ts-expect-error - TS2345 - Argument of type 'DiscussionTab | null' is not assignable to parameter of type 'Element'.
                            ref={(node) => this.captureRef(node, key)}
                            key={props.id}
                            trigger={trigger}
                            isTabSelected={isTabSelected || false}
                            {...props}
                        >
                            {trigger}
                        </DiscussionTab>
                    ),
                    isTabDisabled: false,
                };

                return tabToRender;
            },
            renderContent: () => content,
        });

        return tabs.map(getTab);
    };

    renderPointsUnverifiedEmailAccountAgeUpsell: (
        unverifiedEmail?: string | null | undefined,
    ) => React.ReactNode = (unverifiedEmail?: string | null) => (
        <Body style={styles.tabContent}>
            <UpsellContainer>
                <View>
                    <Body>
                        {t`To get started, your account must be at least 3 days old, have a verified email address, and have at least 5,000 energy points.`}
                    </Body>
                    <Strut size={spacing.medium_16} />
                    {unverifiedEmail && (
                        <ResendVerificationEmailButton
                            email={unverifiedEmail}
                        />
                    )}
                </View>
            </UpsellContainer>
        </Body>
    );

    renderLoginUpsell: () => React.ReactNode = () => {
        let continueUrl = this.props.location.pathname;
        if (continueUrl.indexOf("?") === -1) {
            continueUrl += `?${loggedInQueryName}=true`;
        } else {
            continueUrl += `&${loggedInQueryName}=true`;
        }

        return (
            <View>
                <UpsellContainer>
                    <Button
                        style={styles.loginButton}
                        href={getLoginUrl(continueUrl)}
                    >
                        {t`Log in`}
                    </Button>
                </UpsellContainer>
            </View>
        );
    };

    render(): React.ReactNode {
        if (this.props.shouldShowDiscussionsNotAvailableMessage) {
            return <DiscussionsNotAvailableMessage />;
        }

        const tabs = this.createTabs();
        if (!tabs.length) {
            return null;
        }

        return (
            <Id>
                {(id) => (
                    <View style={styles.container}>
                        <TabbedPanel
                            tabListAriaLabel={t`Discussions`}
                            tabs={this.createRenderers(tabs)}
                            selectedTabKey={this.props.currentTabKey}
                            onTabChanged={this.onTabChangedwithGql}
                            id={id}
                        >
                            {(renderTabs, renderContentPanel) => [
                                renderTabs(
                                    styles.tabList,
                                    styles.tabItemSpacing,
                                ),

                                // This element needs to be a `div` for the
                                // separator to work.
                                <div
                                    className={css(styles.separator)}
                                    key="discussionTabsSeparator"
                                />,

                                renderContentPanel(),
                            ]}
                        </TabbedPanel>
                    </View>
                )}
            </Id>
        );
    }
}

const styles = StyleSheet.create({
    activeTab: {
        color: color.blue,
        ...sharedStyles.navbar.activeTab,
    },

    inactiveTab: {
        ...sharedStyles.navbar.inactiveTab,
    },

    separator: {
        ...sharedDiscussionStyles.separator,
    },

    topSeparator: {
        marginTop: 50,
    },

    container: {
        // HACK(briang): Setting this to static makes it so the select
        // dropdown above this component in the view does not get clipped
        position: "static",
        ...sharedDiscussionStyles.discussionContainer,
        [mediaQueries.smOrSmaller]: {
            padding: 0,
        },
    },

    tabContent: {
        padding: "16px 0px",
        color: color.offBlack,
    },

    tabTrigger: {
        // The zIndex allows the box shadow to sit on top of the separator.
        zIndex: 1,
        height: 48,
        color: color.offBlack,
        boxShadow: `inset 0 -4px`,
    },

    tabSpan: {
        display: "flex !important",
        alignItems: "center",
    },

    tabList: {
        overflowX: "auto",
    },

    tabItemSpacing: {
        ":not(:last-child)": {
            marginRight: 24,
        },
    },

    loginUpsell: {
        textAlign: "center",
        padding: "32px 0 22px 0",
    },

    upsellText: {
        marginBottom: 18,
    },

    loginButtonContainer: {
        flexDirection: "row",
        justifyContent: "center",
    },

    loginButton: {
        minWidth: 138,
    },

    notAButton: {
        ...sharedDiscussionStyles.notAButton,
    },

    englishLink: {
        margin: "20px 0 0 20px",
    },

    spinner: {
        margin: "20px auto",
    },
});

// exported for testing
export const mapStateToProps = (state: State): TabsReduxStateProps => {
    return {
        shouldShowDiscussionsNotAvailableMessage:
            state.fetchingPostsStatus === "failed" &&
            Object.keys(state.posts).length === 0,
        currentTabKey: state.currentTab,
        hasUnansweredHelpRequests: state.hasUnansweredHelpRequests,
        focusOnTopLevelInput: state.focusOnTopLevelInput,
    };
};

const mapDispatchToProps = {
    onPostSuccess: postSuccessAction,
    onTabChanged: changeTabAction,
} as const;

type ExportProps = WithoutUserProfile<
    WithoutLocation<
        WithoutApollo<
            Without<
                JSX.LibraryManagedAttributes<
                    typeof DiscussionTabs,
                    React.ComponentProps<typeof DiscussionTabs>
                >,
                TabsReduxDispatchProps & TabsReduxStateProps
            >
        >
    >
>;

export default compose(
    withLocation,
    withApollo,
    withUserProfile,
    connect(mapStateToProps, mapDispatchToProps),
)(DiscussionTabs) as React.ComponentType<ExportProps>;
